import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session as SupaSession } from "@supabase/supabase-js";

import { supabase } from "@/lib/supabase";

/**
 * Auth del vertical operaciones (/evergreen) con Supabase (magic-link → JWT).
 * Aislado del login viejo del portal (auth-context.tsx): distinto storage,
 * distinto proveedor. Solo /evergreen lo usa.
 */
interface EvergreenAuthValue {
  session: SupaSession | null;
  loading: boolean;
  configured: boolean;
  signInWithEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const EvergreenAuthContext = createContext<EvergreenAuthValue | null>(null);

export function EvergreenAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SupaSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signInWithEmail = useCallback(async (email: string) => {
    if (!supabase) throw new Error("Supabase no está configurado (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY).");
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/evergreen` },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
  }, []);

  return (
    <EvergreenAuthContext.Provider
      value={{ session, loading, configured: supabase !== null, signInWithEmail, signOut }}
    >
      {children}
    </EvergreenAuthContext.Provider>
  );
}

export function useEvergreenAuth(): EvergreenAuthValue {
  const v = useContext(EvergreenAuthContext);
  if (!v) throw new Error("useEvergreenAuth fuera de EvergreenAuthProvider");
  return v;
}

/**
 * Token de acceso (JWT) actual para las llamadas al Engine de operaciones.
 * Lo usa lib/operaciones.ts. Devuelve null si no hay sesión Supabase.
 */
export async function getEvergreenToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
